import { cn } from '../lib/utils';

export interface BackgroundBlobsProps {
  /** Additional className to apply to the container */
  className?: string;
  /** Custom colors for the blobs. If not provided, defaults will be used */
  colors?: {
    primary?: string;
    secondary?: string;
    tertiary?: string;
    quaternary?: string;
    quinary?: string;
    senary?: string;
    septenary?: string;
  };
  /** Whether to show the glass overlay */
  showGlassOverlay?: boolean;
  /** Whether to show the gradient overlay */
  showGradientOverlay?: boolean;
  /** Custom styles for the glass overlay */
  glassOverlayClassName?: string;
  /** Custom styles for the gradient overlay */
  gradientOverlayClassName?: string;
}

export function BackgroundBlobs({
  className,
  colors = {
    primary: '#E9D5FF', // purple-200
    secondary: '#FBCFE8', // pink-200
    tertiary: '#BFDBFE', // blue-200
    quaternary: '#C7D2FE', // indigo-200
    quinary: '#DDD6FE', // violet-200
    senary: '#F5D0FE', // fuchsia-200
    septenary: '#FECDD3', // rose-200
  },
  showGlassOverlay = true,
  showGradientOverlay = true,
  glassOverlayClassName,
  gradientOverlayClassName,
}: BackgroundBlobsProps) {
  return (
    <>
      {/* Background Blobs - Now with mobile responsive opacity */}
      <div
        className={cn(
          'absolute w-[800px] h-[800px] rounded-full blur-[20px] -top-48 -left-48 animate-blob',
          className
        )}
        style={{
          backgroundColor: colors.primary,
          opacity: 0.3,
        }}
      />
      <div
        className='absolute w-[900px] h-[900px] rounded-full blur-[20px] top-1/2 left-[70%] bottom-[60%] -translate-x-1/2 -translate-y-1/2 animate-blob'
        style={{
          backgroundColor: colors.secondary,
          opacity: 0.3,
          animationDelay: '2s',
        }}
      />
      <div
        className='absolute w-[800px] h-[800px] rounded-full blur-[20px] -bottom-48 -right-48 animate-blob'
        style={{
          backgroundColor: colors.tertiary,
          opacity: 0.3,
          animationDelay: '4s',
        }}
      />

      {/* Additional Blobs */}
      <div
        className='absolute w-[700px] h-[700px] rounded-full blur-[30px] top-1/4 right-1/4 animate-blob'
        style={{
          backgroundColor: colors.quaternary,
          opacity: 0.3,
          animationDelay: '1s',
        }}
      />
      <div
        className='absolute w-[750px] h-[750px] rounded-full blur-[30px] bottom-1/4 left-1/4 animate-blob'
        style={{
          backgroundColor: colors.quinary,
          opacity: 0.35,
          animationDelay: '3s',
        }}
      />
      <div
        className='absolute w-[650px] h-[650px] rounded-full blur-[30px] top-1/3 right-1/3 animate-blob'
        style={{
          backgroundColor: colors.senary,
          opacity: 0.3,
          animationDelay: '5s',
        }}
      />
      <div
        className='absolute w-[680px] h-[680px] rounded-full blur-[30px] bottom-1/3 left-1/3 animate-blob'
        style={{
          backgroundColor: colors.septenary,
          opacity: 0.35,
          animationDelay: '6s',
        }}
      />

      {/* Glass Overlay */}
      {showGlassOverlay && (
        <div
          className={cn(
            'absolute inset-0 bg-clip-padding backdrop-filter backdrop-blur-[2px] bg-white/[0.01] pointer-events-none',
            glassOverlayClassName
          )}
        />
      )}

      {/* Gradient Overlay */}
      {showGradientOverlay && (
        <div
          className={cn(
            'absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-white pointer-events-none',
            gradientOverlayClassName
          )}
        />
      )}
    </>
  );
}
